import * as React from 'react';
import { ThemeProvider } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import { toDate } from './dateInput';

import { customTheme } from '../../libs';

// Single-date picker that combines a typed text input (MM/DD/YYYY) with a
// calendar-icon button that opens a popover calendar — MUI's stock
// <DatePicker>, wrapped with our project-wide ThemeProvider and the
// AdapterDateFns localization adapter so callers don't have to set up the
// localization context at every call site.
//
// Accepts either a native Date or a moment.Moment as `date` (normalised via
// toDate at the boundary), and emits a Date | null via handleSetDate when the
// user selects from the calendar OR types a complete valid date.

interface DatePickerProps {
  ariaLabel?: string;
  className?: string;
  date: Date | null | any;
  disablePast?: boolean;
  handleSetDate: (date: Date | null) => void;
  label?: string;
}

export const DatePicker: React.FC<DatePickerProps> = ({ ariaLabel, className, date, disablePast, handleSetDate, label }) => {
  return (
    <ThemeProvider theme={customTheme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <MUIDatePicker
          className={['shared-date-picker-container', className].filter(Boolean).join(' ')}
          disablePast={disablePast}
          format="MM/dd/yyyy"
          label={label}
          onChange={handleSetDate}
          slotProps={{
            textField: {
              'aria-label': ariaLabel,
              placeholder: 'MM/DD/YYYY',
            },
            // Most Rails layouts render without a doctype (quirks mode), where
            // Popper.js's adaptive positioning misreads
            // documentElement.clientHeight and places the popup off-screen on
            // pages taller than the viewport. Non-adaptive positioning uses
            // top/left document coordinates, correct in both modes.
            popper: {
              popperOptions: {
                modifiers: [{ name: 'computeStyles', options: { adaptive: false } }],
              },
            },
          }}
          value={toDate(date)}
        />
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default DatePicker;
